import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Fade,
  alpha,
  useTheme
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatarDinheiro } from '../../helper';

type DebitoItem = {
  fatura: string;
  vencimento: string;
  valor: number;
  linkPagamento: string;
};

type Props = {
  debitosAbertos: DebitoItem[];
  codigoFatura: string;
  corPrincipal: string;
  isMobile: boolean;
};

const TabelaDebitosLinkPagamento: React.FC<Props> = ({
  debitosAbertos,
  codigoFatura,
  corPrincipal,
  isMobile
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const outrasFaturas = debitosAbertos.filter((f) => f.fatura !== codigoFatura);

  if (outrasFaturas.length === 0) return null;

  const faturasExibidas = outrasFaturas.slice(0, 5);

  const handleGerarPix = (linkPagamento: string) => {
    window.scrollTo(0, 0);
    navigate(`/cobranca/${linkPagamento}`);
  };

  return (
    <Fade in={true} timeout={900}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          width: '100%',
          mb: 3
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" mb={1}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: corPrincipal, color: '#fff' }}>
            <FormatListBulletedIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: 'text.primary', lineHeight: 1.2 }}
            >
              Outras faturas em aberto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              Confira abaixo outras faturas que você pode pagar.
            </Typography>
          </Box>
        </Stack>

        {isMobile ? (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: alpha(theme.palette.divider, 0.15),
              overflow: 'hidden'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                px: '12px',
                py: '6px',
                gap: '8px',
                bgcolor: alpha(corPrincipal, 0.06),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{
                  width: '70px',
                  fontSize: '0.65rem',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                FATURA
              </Typography>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{
                  width: '90px',
                  fontSize: '0.65rem',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textAlign: 'right'
                }}
              >
                VENCIMENTO
              </Typography>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{ flex: 1, fontSize: '0.65rem', textAlign: 'right' }}
              >
                VALOR
              </Typography>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', width: '30px', textAlign: 'center' }}
              >
                PIX
              </Typography>
            </Stack>
            {faturasExibidas.map((fatura, index, arr) => (
              <Box key={fatura.linkPagamento}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    px: '12px',
                    py: '8px',
                    gap: '8px',
                    bgcolor:
                      index % 2 === 0 ? 'transparent' : alpha(theme.palette.action.hover, 0.3)
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      width: '70px',
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {fatura.fatura}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      width: '90px',
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textAlign: 'right'
                    }}
                  >
                    {fatura.vencimento}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flex: 1, fontSize: '0.8rem', textAlign: 'right' }}
                  >
                    R$ {formatarDinheiro(fatura.valor)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleGerarPix(fatura.linkPagamento)}
                    sx={{
                      color: corPrincipal,
                      bgcolor: alpha(corPrincipal, 0.08),
                      '&:hover': { bgcolor: alpha(corPrincipal, 0.15) }
                    }}
                  >
                    <QrCodeScannerIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Stack>
                {index < arr.length - 1 && <Divider />}
              </Box>
            ))}
          </Paper>
        ) : (
          <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 550 }}>
              <TableHead
                sx={{
                  bgcolor: alpha(corPrincipal, 0.06),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      py: 1.5
                    }}
                  >
                    FATURA
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      py: 1.5
                    }}
                  >
                    VENCIMENTO
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      py: 1.5
                    }}
                  >
                    VALOR
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      py: 1.5
                    }}
                  >
                    AÇÃO
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faturasExibidas.map((fatura) => (
                  <TableRow
                    key={fatura.linkPagamento}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        py: 2,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      {fatura.fatura}
                    </TableCell>
                    <TableCell sx={{ py: 2, color: 'text.primary' }}>{fatura.vencimento}</TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 'bold', color: 'text.primary' }}>
                      R$ {formatarDinheiro(fatura.valor)}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<QrCodeScannerIcon sx={{ color: corPrincipal }} />}
                        onClick={() => handleGerarPix(fatura.linkPagamento)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'bold',
                          borderColor: alpha(corPrincipal, 0.4),
                          color: corPrincipal,
                          borderRadius: 2,
                          px: 2,
                          '&:hover': {
                            borderColor: corPrincipal,
                            bgcolor: alpha(corPrincipal, 0.05)
                          }
                        }}
                      >
                        Gerar PIX
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {outrasFaturas.length > 5 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.78rem',
              fontStyle: 'italic'
            }}
          >
            Exibindo as 5 faturas em aberto com vencimento mais antigo.
          </Typography>
        )}

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: '#FFF1F0',
            borderRadius: 2,
            border: '1px solid #FFA39E',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#F5222D', fontSize: 18 }} />
          <Typography sx={{ color: '#F5222D', fontSize: '0.8rem', fontWeight: 500 }}>
            Ao clicar em Gerar PIX, você será direcionado para a página de pagamento da fatura
            selecionada.
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};

export default TabelaDebitosLinkPagamento;
